import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { FC, memo } from 'react';
import { Wrapper, Content, Field, Label } from './components';
import { Button, InputNumber, Radio, Space, Tooltip } from 'udesk-ui';
import TaskOperatorListGather from 'src/components/pages/components/task-operator-list-gather';
import Rules from 'src/components/common/Rules';
import { RuleTypeMap } from 'Component/common/Rules/const';
import { HelpOutlined } from '@udesk/icons';
import { TalkerRecognitionConfig } from 'src/api/types';

export const RecognitionMethod = {
    API: 1,
    Text: 2,
};

interface TemplateProps {
    value?: TalkerRecognitionConfig & {
        recognitionMethod?: number;
    };
    onChange?: (v: TemplateProps['value']) => void;
    onSave?: (v: TemplateProps['value']) => void;
}

const Template: FC<TemplateProps> = memo((props) => {
    const { value, onChange, onSave } = props;

    const {
        recognitionMethod = RecognitionMethod.API,
        talkerMinNum = 2,
        talkerMaxNum = 10,
        agentNum = 1,
        operatorList,
        operatorLogic,
        judgeStrategy = RuleTypeMap.CUSTOMIZE,
    } = value || {};

    return (
        <Wrapper>
            <div style={{ flexBasis: '90%' }}>
                <Radio.Group
                    options={[
                        {
                            value: RecognitionMethod.API,
                            label: (
                                <Space size="small" align="center">
                                    <div>
                                        {
                                            /* API标识轨道角色 */ UdeskLocales['current'].pages
                                                .speechRecognition.components.speakerRecognition
                                                .index.aPIIdentifiesTrackRole
                                        }
                                    </div>
                                    <Tooltip
                                        title={
                                            <div style={{ whiteSpace: 'pre-wrap' }}>
                                                {/* 通过API接入第三方的通话录音需要标识左声道是客服还是客户 */}
                                                {
                                                    UdeskLocales['current'].pages.speechRecognition
                                                        .components.speakerRecognition.index
                                                        .toAccessAThirdpartyCallRecordingThroughAPIItIsNecessaryToIdentifyWhetherTheLeftChannelIsACustomerServiceOrACustomer
                                                }
                                            </div>
                                        }
                                    >
                                        <HelpOutlined style={{ fontSize: 16 }} />
                                    </Tooltip>
                                </Space>
                            ),
                        },
                        {
                            value: RecognitionMethod.Text,
                            label: (
                                <Space size="small">
                                    <div>
                                        {
                                            /* 文本识别 */ UdeskLocales['current'].pages
                                                .speechRecognition.components.speakerRecognition
                                                .index.textRecognition
                                        }
                                    </div>
                                    <Tooltip
                                        title={
                                            <div style={{ whiteSpace: 'pre-wrap' }}>
                                                {/* 单通道录音通过文本识别，角色配置定义客服的角色 */}
                                                {
                                                    UdeskLocales['current'].pages.speechRecognition
                                                        .components.speakerRecognition.index
                                                        .singlechannelRecordingDefinesTheRoleOfTheAgentThroughTextRecognitionAndRoleConfiguration
                                                }
                                            </div>
                                        }
                                    >
                                        <HelpOutlined style={{ fontSize: 16 }} />
                                    </Tooltip>
                                </Space>
                            ),
                        },
                    ]}
                    value={recognitionMethod}
                    onChange={(v) => onChange?.({ ...value, recognitionMethod: v.target.value })}
                />

                {recognitionMethod === RecognitionMethod.Text ? (
                    <Content>
                        <div>
                            <Space>
                                <div>
                                    {/* 说话人最小数量 */}
                                    {
                                        UdeskLocales['current'].pages.speechRecognition.components
                                            .speakerRecognition.index.minimumNumberOfSpeakers
                                    }
                                </div>
                                <InputNumber
                                    min={2}
                                    precision={0}
                                    value={talkerMinNum}
                                    onChange={(v) => onChange?.({ ...value, talkerMinNum: v })}
                                />
                                <div>
                                    {/* 说话人最大数量 */}
                                    {
                                        UdeskLocales['current'].pages.speechRecognition.components
                                            .speakerRecognition.index.maximumNumberOfSpeakers
                                    }
                                </div>
                                <InputNumber
                                    min={1}
                                    max={100}
                                    precision={0}
                                    value={talkerMaxNum}
                                    onChange={(v) => onChange?.({ ...value, talkerMaxNum: v })}
                                />
                                <div>
                                    {/* 客服数量 */}
                                    {
                                        UdeskLocales['current'].pages.speechRecognition.components
                                            .speakerRecognition.index.numberOfCustomerService
                                    }
                                </div>
                                <InputNumber
                                    min={1}
                                    precision={0}
                                    value={agentNum}
                                    onChange={(v) => onChange?.({ ...value, agentNum: v })}
                                />
                            </Space>
                        </div>

                        <Field>
                            <Label>
                                {/* 算子： */}
                                {
                                    UdeskLocales['current'].pages.speechRecognition.components
                                        .speakerRecognition.index.operator
                                }
                            </Label>

                            <div style={{ flex: 1 }}>
                                <TaskOperatorListGather
                                    operatorTypes={Udesk.enums.operatorTypes.filter((item) =>
                                        [
                                            Udesk.enums.operatorTypes.keyword.id,
                                            Udesk.enums.operatorTypes.keywordStrict.id,
                                            Udesk.enums.operatorTypes.regular.id,
                                            Udesk.enums.operatorTypes.semantics.id,
                                            Udesk.enums.operatorTypes.semanticTags.id,
                                            Udesk.enums.operatorTypes.conceptWord.id,
                                        ].includes(item.id)
                                    )}
                                    operatorList={operatorList}
                                    onChange={(v) =>
                                        onChange?.({
                                            ...value,
                                            operatorList: v.map((_v, idx) => {
                                                return {
                                                    ..._v,
                                                    idCode: `R${idx + 1}`,
                                                };
                                            }),
                                        })
                                    }
                                />
                            </div>
                        </Field>

                        <Field>
                            <Label>
                                {/* 规则： */}
                                {
                                    UdeskLocales['current'].pages.speechRecognition.components
                                        .speakerRecognition.index.rules
                                }
                            </Label>

                            <div style={{ flex: 1 }}>
                                <Rules
                                    operatorList={operatorList?.map((item, idx) => {
                                        return {
                                            ...item,
                                            idCode: `R${idx + 1}`,
                                        };
                                    })}
                                    value={[
                                        {
                                            formula: operatorLogic,
                                            judgeStrategy,
                                        },
                                    ]}
                                    onChange={(v) =>
                                        onChange?.({
                                            ...value,
                                            operatorLogic: v?.[0].formula,
                                            judgeStrategy:
                                                v?.[0].judgeStrategy ?? RuleTypeMap.CUSTOMIZE,
                                        })
                                    }
                                />
                            </div>
                        </Field>
                    </Content>
                ) : null}
            </div>

            <Button
                type="primary"
                size="small"
                onClick={() =>
                    onSave?.({
                        recognitionMethod,
                        talkerMinNum,
                        talkerMaxNum,
                        agentNum,
                        operatorList,
                        operatorLogic,
                        judgeStrategy,
                    })
                }
            >
                {/* 保存 */}
                {
                    UdeskLocales['current'].pages.speechRecognition.components.speakerRecognition
                        .index.preserve
                }
            </Button>
        </Wrapper>
    );
});

export default Template;
